import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AdminUrlBuilder } from '../../../../../shared/admin/api/admin-url-builder';
import { AbstractListComponent } from '../../../../../shared/crud/component/list.component';
import { BreadcrumbBuilder } from '../../../../../shared/ui/breadcrumb/breadcrumb-builder';
import { IDropdownSearcherOptions } from '../../../../../shared/ui/component/dropdown-searcher/dropdown-searcher.interfaces';
import {
    PRINT_TEMPLATE_TYPES,
    PrintTemplateType,
} from '../../../../../shared/print-template/print-template.model';
import { PrintTemplateAssetUploader } from '../../../../../shared/print-template/print-template-asset-uploader';
import { CampaignOption } from '../../print-credit/types';
import {
    IConfirmationDialogPayload,
} from '../../../../../shared/ui/component/confirmation-dialog/confirmation-dialog-payload.interface';

const ALL_CAMPAIGNS: CampaignOption = { value: 0, label: 'All campaigns', uuid: '', id: 0 };

@Component({
    selector: 'admin-print-template-list',
    templateUrl: './list.component.html',
    styleUrls: [ './list.component.scss' ],
})
export class AdminPrintTemplateListComponent extends AbstractListComponent implements OnInit {

    types = PRINT_TEMPLATE_TYPES;
    /** One type at a time — keyring and snowglobe templates are never listed together. */
    selectedType: PrintTemplateType = 'keyring';

    dropdownSearchOptions: IDropdownSearcherOptions = {
        minSearchPhraseLength: 0,
        dropdownMaxHeight: '420px',
        placeholder: 'Select campaign',
    };

    private allCampaigns: BehaviorSubject<CampaignOption[]> = new BehaviorSubject<CampaignOption[]>([ ALL_CAMPAIGNS ]);
    private visibleCampaigns: BehaviorSubject<CampaignOption[]> = new BehaviorSubject<CampaignOption[]>([]);
    campaigns$: Observable<CampaignOption[]> = this.visibleCampaigns.asObservable();

    private loadedRows: any[] = [];
    private selectedCampaign: CampaignOption;

    get selectedCampaignId(): number {
        return this.parameters.conditions.campaign;
    }

    constructor(httpClient: HttpClient,
                toastrService: ToastrService,
                router: Router,
                route: ActivatedRoute,
                urlBuilder: AdminUrlBuilder,
                formBuilder: FormBuilder,
                private assetUploader: PrintTemplateAssetUploader,
                private breadcrumbBuilder: BreadcrumbBuilder) {
        super(httpClient, toastrService, router, route, urlBuilder);
    }

    ngOnInit(): void {
        this.breadcrumbBuilder.clear();
        this.breadcrumbBuilder.add({
            label: 'Admin / Print templates',
            url: '/admin/print-template',
        });

        this.parameters.limit = 10;
        this.parameters.offset = 0;
        if (this.route.snapshot.queryParams.campaign) {
            this.parameters.conditions.campaign = +this.route.snapshot.queryParams.campaign;
        }
        this.selectedType = this.route.snapshot.queryParams.type || this.selectedType;
        this.parameters.conditions.type = this.selectedType;
        this.loadCampaigns();

        // The inherited delete() reports the outcome here and reloads the list straight after, so
        // this is where the paging is put back to the first page — and only when the delete went
        // through. A failed one leaves the accumulated pages exactly as they were, otherwise the
        // next Load More would ask for offset 1 again and show a page twice.
        this.deleteSubject.subscribe((payload: IConfirmationDialogPayload) => {
            if (payload.result) {
                this.loadedRows = [];
                this.parameters.offset = 0;
            }
        });

        // A template always belongs to a campaign, so there is nothing to show until one is picked.
        if (this.selectedCampaignId) {
            super.ngOnInit();
        }
    }

    getResourceUrl(): string {
        return '/print-template';
    }

    /** Rows accumulate as more pages are pulled in, the way the photographer list behaves. */
    load(): Subscription {
        this.loading = true;
        if (this.loadingPromise) {
            this.loadingPromise.unsubscribe();
        }
        this.loadingPromise = this.fetchRemoteSource().subscribe();
        this.loadingPromise.add(() => {
            this.pagination.rows = [ ...this.loadedRows, ...this.pagination.rows ];
            this.loading = false;
        });

        return this.loadingPromise;
    }

    loadMore(): void {
        this.loadedRows = [ ...this.pagination.rows ];
        this.parameters.offset += 1;
        this.load();
    }

    get hasMore(): boolean {
        return this.pagination.rows.length < this.pagination.count;
    }

    private reload(): void {
        this.loadedRows = [];
        this.parameters.offset = 0;
        if (!this.selectedCampaignId) {
            this.resetPaginationObject();
            return;
        }
        this.load();
    }

    private loadCampaigns(): void {
        this.httpClient.get<any>(this.urlBuilder.buildUrl('/campaign') + '/all').subscribe(
            (response: any) => {
                const campaigns = (response.campaigns || []).map((campaign: any) => ({
                    value: campaign.id,
                    label: campaign.name,
                    uuid: campaign.uuid,
                    id: campaign.id,
                }));
                campaigns.sort((a: CampaignOption, b: CampaignOption) => a.label.localeCompare(b.label));

                this.allCampaigns.next([ ALL_CAMPAIGNS, ...campaigns ]);
                this.visibleCampaigns.next(this.allCampaigns.getValue());
                // The campaign can come from the URL, and the upload endpoint needs its uuid.
                this.selectedCampaign = campaigns.filter(
                    (campaign: CampaignOption) => campaign.value === this.selectedCampaignId)[ 0 ];
            },
            () => {
                this.visibleCampaigns.next(this.allCampaigns.getValue());
            }
        );
    }

    searchCampaign(phrase: string): void {
        const campaigns = this.allCampaigns.getValue();
        if (!phrase) {
            this.visibleCampaigns.next(campaigns);
            return;
        }
        this.visibleCampaigns.next(
            campaigns.filter((campaign: CampaignOption) => campaign.label.toLowerCase().includes(phrase.toLowerCase()))
        );
    }

    selectCampaign(campaign: CampaignOption): void {
        this.selectedCampaign = campaign.value ? campaign : null;
        if (campaign.value) {
            this.parameters.conditions.campaign = campaign.value;
        } else {
            delete this.parameters.conditions.campaign;
        }
        this.storeFilters();
        this.reload();
    }

    setType(type: PrintTemplateType): void {
        if (type === this.selectedType) {
            return;
        }
        this.selectedType = type;
        this.parameters.conditions.type = type;
        this.storeFilters();
        this.reload();
    }

    /** Legacy templates are history — new ones always name the photo ratio they are built for. */
    /** The campaign the uploads belong to, whether it was picked in the dropdown or read from the url. */
    private get campaignUuid(): string {
        if (this.selectedCampaign && this.selectedCampaign.uuid) {
            return this.selectedCampaign.uuid;
        }
        const match = this.allCampaigns.getValue()
            .find((campaign: CampaignOption) => campaign.value === this.selectedCampaignId);

        return match ? match.uuid : '';
    }

    get canAdd(): boolean {
        return !!this.selectedCampaignId;
    }

    /**
     * Creates straight away: the panel uploads the artwork and both example photos, then posts. Name,
     * type and ratio describe what the template is and cannot be changed afterwards, so they are all
     * settled before the row exists.
     */
    addTemplate(): void {
        if (!this.canAdd) {
            return;
        }
        if (!this.selectedCampaignId) {
            this.toastrService.error('Pick a campaign first');
            return;
        }
        if (!this.campaignUuid) {
            this.toastrService.error('That campaign has no uuid, so its files cannot be uploaded');
            return;
        }
        if (this.pagination.rows.some((row: any) => row.isDraft)) {
            this.toastrService.warning('Finish the template you started first');
            return;
        }

        // A draft only: the template, its artwork and its example photos all reach the API together
        // when staff saves, so nothing half made is left behind if they change their mind.
        this.pagination.rows = [ {
            id: 0,
            isDraft: true,
            name: this.typeLabel(this.selectedType) + ' template',
            type: this.selectedType,
            campaign: {
                id: this.selectedCampaignId,
                name: this.selectedCampaign ? this.selectedCampaign.label : '',
                uuid: this.campaignUuid,
            },
            isDefault: false,
            isActive: true,
            defaultTemplate: false,
            file: null,
            positions: null,
        }, ...this.pagination.rows ];
    }

    /** Swaps the draft row for the template the API created, so the list stops showing a placeholder. */
    onCreated(): void {
        this.pagination.rows = this.pagination.rows.filter((row: any) => !row.isDraft);
        this.loadedRows = [ ...this.pagination.rows ];
        this.reload();
    }

    /** Keeps each row with its own editor, so inserting a draft on top does not reshuffle them. */
    trackRow(index: number, row: any): any {
        return row.isDraft ? 'draft' : row.id;
    }

    /**
     * The row goes with the template, so the list does not keep showing something that is gone.
     * Deleting shifts every later row up on the server, so the pages pulled in so far no longer
     * line up with it: reloading only the last offset would skip whatever moved across a page
     * boundary. The list goes back to the first page instead — see the ngOnInit subscription —
     * and Load More walks it again.
     */
    onDelete(row: any): void {
        this.delete(row.id);
    }

    discardDraft(): void {
        this.pagination.rows = this.pagination.rows.filter((row: any) => !row.isDraft);
    }

    private storeFilters(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                type: this.selectedType,
                    campaign: this.selectedCampaignId || null,
            },
            queryParamsHandling: 'merge',
        });
    }

    /** Default is scoped per type and per ratio, so one choice never unsets an unrelated template. */
    makeDefault(row: any): void {
        this.httpClient
            .put(this.urlBuilder.buildUrl('/print-template') + '/' + row.id + '/default', { isDefault: true })
            .subscribe(
                () => {
                    this.pagination.rows.forEach((other: any) => other.isDefault = other.id === row.id);
                    this.toastrService.success('Saved');
                },
                () => this.toastrService.error('Failed to change the default template')
            );
    }

    get hasDefault(): boolean {
        return this.pagination.rows.some((row: any) => row.isDefault);
    }

    /**
     * Having no default at all is a valid state: the ratio then falls back to whatever the print
     * flow does without one, the same way the CMS lets a default be switched off.
     */
    clearDefault(): void {
        const current = this.pagination.rows.filter((row: any) => row.isDefault)[ 0 ];
        if (!current) {
            return;
        }

        this.httpClient
            .put(this.urlBuilder.buildUrl('/print-template') + '/' + current.id + '/default', { isDefault: false })
            .subscribe(
                () => {
                    this.pagination.rows.forEach((row: any) => row.isDefault = false);
                    this.toastrService.success('Saved');
                },
                () => this.toastrService.error('Failed to change the default template')
            );
    }


    typeLabel(type: string): string {
        const found = this.types.filter((option) => option.value === type)[ 0 ];
        return found ? found.label : type;
    }
}
